import type { ClientFactsFailure, ClientPublishStatus } from "../../data/clientFactsQueries";
import type { FactsField, FactsItemField } from "../../lib/facts/types";
import { valueProblemSentence } from "../../lib/facts/words";

/**
 * The words of Webbplatsens fakta, the client's facts form, build round 19 (2026-10-08).
 *
 * The client is told what happened in its own terms. The sentences for the refusals and for the
 * build's status were decided with the round's contract on 2026-10-08, the daily cap's word for word
 * by the planning chat; `app/lib/errors.ts` keeps the portal-wide sentence for each code, for
 * anywhere else one surfaces. A code not listed here is said in that portal-wide sentence, which is
 * never a raw database string.
 *
 * Every sentence uses "ni", because the facts are the company's (design.md section 3, forms of
 * address). Nothing here mentions a schema, a revision or a repository: those are Petrovic's words.
 */

export const PAGE_TITLE = "Webbplatsens fakta";

/** design.md section 3, rule 3: the one line behind the heading's question mark. */
export const PAGE_HINT =
  "Här ändrar ni uppgifterna på er webbplats, som öppettider och priser. Vill ni ändra något annat, skriv till oss i chatten.";

/** Beside a field only Petrovic changes. "Skriv till oss i chatten" is the link to `/chat`. */
export const LOCKED_QUESTION = "Vill ni ändra det här?";
export const LOCKED_LINK = "Skriv till oss i chatten";

export const SAVE_LABEL = "Spara utkast";
export const SAVE_PENDING = "Sparar";
export const PUBLISH_LABEL = "Publicera på webbplatsen";
export const PUBLISH_PENDING = "Publicerar";

/**
 * "Väntar på fakta", build round 20 (2026-10-08): the website's schema changed and Petrovic fills in
 * its new fields before the site can be built. The status line's words for a publish held back for
 * it, and for a failed build while it is so.
 */
export const WAITING_FOR_FACTS_STATUS = "Väntar på fakta";

/** The build the client started, as its status says. Decided with the contract, 2026-10-08. */
export const PUBLISH_STATUS: Record<ClientPublishStatus, string> = {
  queued: "Publiceras",
  dispatched: "Publiceras",
  succeeded: "Publicerad, syns på webbplatsen om några minuter",
  failed: "Publiceringen gick inte igenom. Vi har fått besked och hör av oss.",
  blocked_waiting_for_facts: WAITING_FOR_FACTS_STATUS
};

/** `{"outcome": "blocked"}`, for either reason: only Petrovic can fix it, and he has been told. */
export const PUBLISH_BLOCKED =
  "Webbplatsen kan inte publiceras just nu. Vi har fått besked och hör av oss. Ändringarna finns kvar här, och ni kan spara dem som utkast.";

/**
 * `{"outcome": "blocked", "reason": "waiting_for_facts"}`, build round 20 (2026-10-08), word for word
 * as the round decided. The site's schema changed and Petrovic is filling in its new fields; the
 * typed changes stay on the form.
 */
export const PUBLISH_WAITING_FOR_FACTS = "Vi håller på att uppdatera webbplatsen. Försök igen senare eller skriv till oss.";

/** The sentence a blocked publish is answered with, by the gateway's reason. */
export function blockedSentence(reason: string): string {
  return reason === "waiting_for_facts" ? PUBLISH_WAITING_FOR_FACTS : PUBLISH_BLOCKED;
}

/** The browser's own check refused, so nothing was sent. */
export const CHECKED_LOCALLY = "Några av uppgifterna behöver rättas. De är markerade vid fälten. Ingenting skickades.";

/** The server's check refused, and its problems are marked at the fields. */
const INVALID = "Några av uppgifterna behöver rättas. De är markerade vid fälten. Ingenting sparades.";

const CHANGED_UNDERNEATH = "Uppgifterna har ändrats sedan sidan öppnades. Ladda om sidan och gör ändringen igen.";

const REFUSAL: Record<string, string> = {
  PORTAL_SITE_PUBLISH_CLIENT_DAILY_CAP:
    "Ni har publicerat fem gånger i dag. Försök igen i morgon, eller skriv till oss om det är bråttom.",
  PORTAL_SITE_PUBLISH_MONTHLY_CAP: "Publiceringarna är pausade resten av månaden. Skriv till oss om det är bråttom.",
  PORTAL_SITE_PUBLISH_ALREADY_QUEUED: "En publicering väntar redan på att skickas. Försök igen om en minut.",
  PORTAL_SITE_FACTS_UNCHANGED: "Inget har ändrats sedan den senaste publiceringen.",
  PORTAL_SITE_FACTS_SCHEMA_CHANGED: CHANGED_UNDERNEATH,
  PORTAL_SITE_FACTS_DRAFT_CHANGED: CHANGED_UNDERNEATH,
  PORTAL_SITE_FACTS_LOCKED: "Några av uppgifterna kan bara Petrovic Systems ändra. Ladda om sidan.",
  PORTAL_SITE_FACTS_INVALID: INVALID,
  PORTAL_FORBIDDEN_ROLE: "Bara en behörig kontaktperson kan ändra uppgifterna.",
  /*
   * Not in the contract's table, added by the client screen's builder. A write that names a tenant
   * the caller cannot write to is, for a member of it, a company whose management is paused or
   * ending; the form is not offered then, so this is a page opened before the state changed. The
   * portal-wide sentence, "Du har inte åtkomst till det här kundkontot", would say something else.
   */
  PORTAL_FORBIDDEN_TENANT: "Det går inte att ändra uppgifterna just nu. Ladda om sidan.",
  // The portal-wide sentence calls the facts "Faktan", which is Petrovic's word for them.
  PORTAL_SITE_FACTS_TOO_LARGE: "Uppgifterna är för långa för att sparas. Ingenting sparades."
};

export function refusalSentence(failure: ClientFactsFailure): string {
  return REFUSAL[failure.code] ?? failure.message;
}

/**
 * Who can change the facts, for somebody who cannot: one line, from the read's `editors`. Swedish
 * lists join the last two with "och" and the others with a comma.
 */
export function editorsLine(editors: string[]): string {
  if (editors.length === 0) {
    return "Bara en behörig kontaktperson kan ändra det här.";
  }
  const names =
    editors.length === 1 ? editors[0] : `${editors.slice(0, -1).join(", ")} och ${editors[editors.length - 1]}`;
  return `Det här kan ändras av ${names}.`;
}

/**
 * A problem beside a field, in the client's words. The client never meets a schema, so the few
 * sentences that name one are said without it; every other sentence is the shared one, which already
 * speaks of the value and not of the contract.
 */
export function clientProblemSentence(code: string, field?: FactsField | FactsItemField): string {
  switch (code) {
    case "not_allowed_value":
      return "Välj ett av alternativen i listan.";
    case "unknown_key":
      return "Uppgiften finns inte längre på webbplatsen.";
    case "price_fixed":
      return "Från-pris och moms kan bara Petrovic Systems ändra.";
    case "locked":
      return "Det här kan bara Petrovic Systems ändra.";
    default:
      return valueProblemSentence(code, field);
  }
}

/** Who published a version, as the client reads it. Petrovic's are "Petrovic". */
export function publishedByName(origin: "admin" | "client", name: string | null): string {
  if (name) {
    return name;
  }
  return origin === "client" ? "Någon hos er" : "Petrovic Systems";
}

/** What changed from the version before, by the fields' labels. */
export function changedLine(changed: string[] | null): string {
  if (changed === null) {
    return "Första versionen";
  }
  return changed.length > 0 ? `Ändrade: ${changed.join(", ")}` : "Inga synliga ändringar";
}
